import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, DialogContent, Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, FieldValues } from 'react-hook-form';
import { captureWorkspacePaymentInfo, isoCountries, IWorkspacePaymentInfo } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDialog,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  ControlledTextInput,
  WsTypoVariants,
  Label,
} from 'wisestep-base';
import { object, string } from 'yup';

import { indianStates } from '../../../utils/IndianStates';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useCustomPlanStore } from '../store/customPlanStore';

const validationSchema = object().shape({
  country: string().required('Country is required'),
});

const billingFormValidationSchema = object().shape({
  gst: string().nullable(),
  address: string().required('Address is required'),
  pincode: string().required('Pincode is required'),
  state: string().required('State is required'),
  city: string().required('City is required'),
});

const CountrySelectionModal = ({
  open,
  feature,
  handleClose,
  getCountry,
  workspaceBillingInfo,
  onSave,
}: {
  open: boolean;
  feature: string;
  handleClose: () => void;
  getCountry: (country: string, feature: string, manageUser: boolean, sameState?: boolean) => void;
  workspaceBillingInfo: IWorkspacePaymentInfo | null;
  onSave: (data: IWorkspacePaymentInfo) => void;
}) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const enableRazorPayEntPlan = useCustomPlanStore((state) => state.enableRazorPayEntPlan);
  const [countries, setCountries] = useState<WsSelectObj[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    enableRazorPayEntPlan ? 'India' : null
  );
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      country: enableRazorPayEntPlan ? 'India' : workspaceBillingInfo?.country,
    },
  });
  const country = methods.watch('country');

  const billindFormMethods = useForm({
    resolver: yupResolver(billingFormValidationSchema),
    defaultValues: {
      country: workspaceBillingInfo?.country,
      gst: workspaceBillingInfo?.gst,
      gst1: workspaceBillingInfo?.gst,
      address: workspaceBillingInfo?.address,
      pincode: workspaceBillingInfo?.pincode,
      state: workspaceBillingInfo?.state,
      city: workspaceBillingInfo?.city,
    },
  });

  useEffect(() => {
    billindFormMethods.setValue('country', country);
  }, [billindFormMethods, country]);

  const { handleSubmit } = methods;

  useEffect(() => {
    const countriesArray: WsSelectObj[] = Object.values(isoCountries).map((country) => ({
      label: country,
      value: country,
    }));
    setCountries(countriesArray);
  }, []);

  const setCountry = async (data: FieldValues) => {
    const payload = {
      country: data.country,
    };
    onSave(payload);
    if (data.country === 'India') {
      setSelectedCountry(data.country);
    } else {
      await captureWorkspacePaymentInfo(payload);
      getCountry(data.country, feature, false);
    }
  };

  const setOtherInfo = async (data: FieldValues) => {
    if (data.gst === data.gst1) {
      const sameState = data.state === 'Telangana';
      if (selectedCountry) {
        const payload = {
          country: selectedCountry,
          gst: data.gst,
          address: data?.address,
          pincode: data.pincode,
          state: data.state,
          city: data.city,
        };
        await captureWorkspacePaymentInfo(payload);
        onSave(payload);
        setSelectedCountry(data.country);
      }
      getCountry(data.country, feature, false, sameState);
    } else {
      setNotification(failed("GST Numbers didn't match !"));
    }
  };

  return (
    <WsDialog size="small" openDialog={open} onClose={handleClose}>
      <DialogContent>
        <CloseIcon sx={{ float: 'right' }} onClick={handleClose} />
        {selectedCountry ? (
          <FormProvider {...billindFormMethods}>
            <form onSubmit={billindFormMethods.handleSubmit(setOtherInfo)}>
              <Grid container justifyContent="center">
                <Grid item xs={12} textAlign="center">
                  <WsTypography variant={WsTypoVariants.TITLE} bold>
                    GST Details*
                  </WsTypography>
                  <Stack direction="row" mt="2">
                    <InfoOutlinedIcon sx={{ mr: 1.5 }} />
                    <WsTypography variant={WsTypoVariants.BODY1} bold>
                      To claim Input Credit, Please provide your GSTIN and Billing Address before
                      proceeding to make the payment. If you don’t have a GST number, please provide
                      your billing address.
                    </WsTypography>
                  </Stack>
                </Grid>
                <Grid item xs={12} textAlign="center" mt={1}>
                  <ControlledTextInput
                    name="gst1"
                    labelText="GST Number"
                    type="password"
                    fullWidth
                    id="gst"
                    placeholder="Enter your GST number"
                    variant="outlined"
                    autoComplete="gst"
                    sx={{ mb: 1.6 }}
                  />
                  <ControlledTextInput
                    name="gst"
                    labelText="Confirm GST Number"
                    type="text"
                    fullWidth
                    id="gst"
                    placeholder="Enter your GST number"
                    variant="outlined"
                    autoComplete="gst"
                    sx={{ mb: 1.6 }}
                  />
                  <ControlledTextInput
                    name="address"
                    labelText="Billing Address"
                    type="text"
                    fullWidth
                    id="address"
                    placeholder="Enter your address"
                    variant="outlined"
                    autoComplete="address"
                    inputProps={{ maxLength: 200 }}
                    required
                  />
                  <Stack direction="row" spacing={2} mt={1}>
                    <Box width="50%">
                      <ControlledTextInput
                        name="country"
                        labelText="Country"
                        type="text"
                        fullWidth
                        id="country"
                        placeholder=""
                        variant="outlined"
                        autoComplete="country"
                        disabled={true}
                        inputProps={{ style: { padding: '9px 14px' } }}
                      />
                    </Box>
                    <Box width="50%">
                      <Label>State</Label>
                      <WsSelect
                        name="state"
                        selectArray={indianStates}
                        placeholder=""
                        size="small"
                        showMandatorySymbol={true}
                        required
                      />
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} mt={2}>
                    <Box width="50%">
                      <ControlledTextInput
                        name="city"
                        labelText="City"
                        type="text"
                        fullWidth
                        id="city"
                        placeholder="Enter your City"
                        variant="outlined"
                        autoComplete="city"
                        required
                      />
                    </Box>
                    <Box width="50%">
                      <ControlledTextInput
                        name="pincode"
                        labelText="Pincode"
                        type="text"
                        fullWidth
                        id="pincode"
                        placeholder="Enter pincode"
                        variant="outlined"
                        autoComplete="pincode"
                        required
                      />
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} mt={3} textAlign="right">
                  <WsButton type="submit">Continue</WsButton>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(setCountry)}>
              <Grid container justifyContent="center">
                <Grid item xs={12} textAlign="center">
                  <LanguageIcon sx={{ color: WsColors.TheBlues500 }} />
                </Grid>
                <Grid item xs={12} textAlign="center" mt={1}>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    Which country is your business registered in ?
                  </WsTypography>
                  <WsTypography color={WsTypoColors.SECONDARY}>
                    Wisestep will use this location for Tax Calculation
                  </WsTypography>
                </Grid>
                <Grid item xs={3.9} mt={2}>
                  <WsSelect
                    name="country"
                    selectArray={countries}
                    placeholder="Select Country"
                    showMandatorySymbol={true}
                    required
                  />
                </Grid>
                <Grid item xs={12} mt={3} textAlign="center">
                  <WsButton type="submit">Continue</WsButton>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        )}
      </DialogContent>
    </WsDialog>
  );
};

export default CountrySelectionModal;
